import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { ContactFields, type ContactDraft } from "../components/ContactFields.js";
import { Button, Chips, Field, inputStyle, Notice } from "../components/ui.js";
import { useAuth } from "../lib/auth.js";
import { cleanFlatNo, cleanPhone, contactProblem } from "../lib/contact.js";
import { requestToJoin, saveContactDetails } from "../lib/members.js";
import { APP_NAME, SPORT, SPORT_LABEL } from "../lib/sport.js";
import { SEED_LEVELS, type MemberCategory, type PlayingAs, type SeedLevel } from "../lib/types.js";

/**
 * The join request. Everything here is a starting point, not a claim — the self-assessed
 * level only sets where the rating begins, and about ten matches of high RD move a player
 * to wherever they actually belong. The copy says that, because otherwise people either
 * sandbag or flatter themselves.
 *
 * There is no "which sports do you play" question any more: you are standing in the app
 * for one of them. The level asked for is the level in *this* sport, which is a better
 * question than the one it replaced.
 *
 * The flat number and phone number are optional and asked last. They are a separate write —
 * `member_contacts` is its own table so that only organisers and admins can read it — and it
 * needs the member row to exist, so it follows the join request rather than riding in it.
 */
export function JoinRequest() {
  const { userId, email, avatarUrl, reloadMember } = useAuth();
  const [displayName, setDisplayName] = useState("");
  const [level, setLevel] = useState<SeedLevel>("casual");
  const [hand, setHand] = useState<"left" | "right" | null>(null);
  const [category, setCategory] = useState<MemberCategory | null>(null);
  // Self-declared here, and only here: after this, only an admin changes it (20260930000100,
  // 20261001000200). Adult by default, because that is what nearly everybody joining is.
  const [as, setAs] = useState<PlayingAs>("adult");
  const junior = as === "junior";
  const [contact, setContact] = useState<ContactDraft>({ flatNo: "", phone: "" });
  // The join went in and the details did not. Said before moving on, because the next screen
  // is the waiting room and nothing there would mention it.
  const [contactFailed, setContactFailed] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: async () => {
      await requestToJoin(userId!, {
        displayName,
        sports: [SPORT],
        selfAssessed: level,
        category: category!,
        junior,
        senior: as === "senior",
        dominantHand: hand,
        preferredSide: null,
        avatarUrl,
      });

      if (cleanFlatNo(contact.flatNo) === null && cleanPhone(contact.phone) === null) return true;
      try {
        await saveContactDetails(contact.flatNo, contact.phone);
        return true;
      } catch (error) {
        setContactFailed(error instanceof Error ? error.message : String(error));
        return false;
      }
    },
    onSuccess: (saved) => {
      if (saved) void reloadMember();
    },
  });

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    submit.mutate();
  }

  // Category has no sensible default, so it has none. Required rather than pre-selected:
  // a pre-selected answer is one people submit without reading.
  const valid =
    displayName.trim().length >= 2 &&
    category !== null &&
    contactProblem(contact.flatNo, contact.phone) === null;

  return (
    <main
      className="mx-auto max-w-md px-4 pb-16"
      style={{ paddingTop: "calc(env(safe-area-inset-top) + 2.5rem)" }}
    >
      <div className="micro mb-2">Signed in as {email}</div>
      <h1 className="font-display mb-3 text-3xl leading-tight" style={{ fontWeight: 800 }}>
        Ask to join {APP_NAME}
      </h1>
      <p className="mb-8 text-ink-muted">Tell us who you are and how you play.</p>

      <div className="tramline mb-8" />

      <form onSubmit={onSubmit}>
        <Field label="Display name" hint="How you appear on the leaderboard.">
          <input
            required
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            style={inputStyle}
            maxLength={60}
          />
        </Field>

        <Field
          label={`Your ${SPORT_LABEL.toLowerCase()} level`}
          hint="Only sets your starting point. Your first ten or so matches move you fast."
        >
          <div className="flex flex-col gap-2">
            {SEED_LEVELS.map((seed) => (
              <button
                key={seed.value}
                type="button"
                onClick={() => setLevel(seed.value)}
                aria-pressed={level === seed.value}
                className="text-left"
                style={{
                  minHeight: 56,
                  padding: "10px 14px",
                  borderRadius: "var(--radius-control)",
                  border: `2px solid ${
                    level === seed.value ? "var(--color-line)" : "var(--color-line-muted)"
                  }`,
                }}
              >
                <span style={{ fontWeight: level === seed.value ? 600 : 400 }}>{seed.label}</span>
                <span className="block text-sm text-ink-muted">{seed.hint}</span>
              </button>
            ))}
          </div>
        </Field>

        {/* Asked before the category, because a junior's reads boy/girl. It decides which
            events somebody may enter, and the member can change it later on their profile
            (20261001000700). A div rather than a Field: Field is a <label>, and a
            label around buttons lends its text to the first one. */}
        <div className="mb-6" role="group" aria-labelledby="playing-as">
          <span id="playing-as" className="field-label">
            Playing as
          </span>
          <Chips
            options={[
              { value: "junior" as const, label: "Junior" },
              { value: "adult" as const, label: "Adult" },
              { value: "senior" as const, label: "Senior" },
            ]}
            selected={[as]}
            onToggle={setAs}
          />
          <span className="mt-1.5 block text-sm text-ink-muted">
            Juniors enter boys&rsquo; and girls&rsquo; events. Adults enter men&rsquo;s and
            women&rsquo;s. Seniors enter the senior events as well as men&rsquo;s and women&rsquo;s.
            Only an admin can change this later.
          </span>
        </div>

        <Field
          label="Category"
          hint="Sets which board you are ranked on, which events you can enter, and whether a match counts: men's and women's matches move your rating, mixed ones are recorded but do not. Only an admin can change it once set."
        >
          {/* Boy/girl for a junior, men/women otherwise — the same two stored values. */}
          <Chips
            options={[
              { value: "men" as const, label: junior ? "Boy" : "Men" },
              { value: "women" as const, label: junior ? "Girl" : "Women" },
            ]}
            selected={category ? [category] : []}
            onToggle={(value) => setCategory(value)}
          />
        </Field>

        <Field label="Dominant hand">
          <Chips
            options={[
              { value: "right" as const, label: "Right" },
              { value: "left" as const, label: "Left" },
            ]}
            selected={hand ? [hand] : []}
            onToggle={(value) => setHand((current) => (current === value ? null : value))}
          />
        </Field>

        <p className="mb-4 text-sm text-ink-muted">
          So organisers can reach you about a match. Only organisers and admins see these, and some
          tournaments ask for them before you can enter.
        </p>
        <ContactFields value={contact} onChange={setContact} />

        <Button
          tone="primary"
          type="submit"
          className="w-full"
          disabled={!valid || submit.isPending || contactFailed !== null}
        >
          {submit.isPending ? "Sending…" : "Send join request"}
        </Button>

        {submit.error ? (
          <p className="mt-4 text-sm" style={{ color: "var(--color-attention)" }}>
            {submit.error.message}
          </p>
        ) : null}
      </form>

      {contactFailed ? (
        <div className="mt-6">
          <Notice tone="attention">
            Your request to join has been sent, but your contact details weren&rsquo;t saved (
            {contactFailed}). You can add them on your profile.
          </Notice>
          <Button className="w-full" onClick={() => void reloadMember()}>
            Continue
          </Button>
        </div>
      ) : null}
    </main>
  );
}
